/**
 * Human-readable summaries for shell commands on a tool-call row.
 *
 * A transcript full of `Ran grep -rn "Mark as blocked\|markBlocked" web/src
 * --include=*.ts | head -20` rows is a transcript nobody reads: the row is
 * fixed-width, so the flags survive and the *subject* gets clipped. This module
 * turns a command into what it did — "Searched for `Mark as blocked` in
 * web/src" — so the row carries the intent and the exact command stays one hover
 * (or one click) away. Nothing here changes what happened; it only changes what
 * the row leads with.
 *
 * Three rules keep it honest:
 *
 * 1. **Translate syntax, not English.** `sed -n '440,520p' file` hides a ranged
 *    read behind an address expression, so it earns a summary. `cat file`
 *    already reads as itself, and the file-op layer ({@link ./shell-file-ops.ts})
 *    owns turning that row into a real Read. Commands that are already plain
 *    stay unrecognized here, so there is only ever one Read presentation.
 * 2. **The headline describes the first thing that ran.** Agents chain commands
 *    (`git status && git log && gh pr list`), so the row leads with the first
 *    significant command and says how many followed. If that first command
 *    isn't one we recognize, the whole row keeps its plain rendering rather
 *    than headlining with something buried in the middle.
 *    The same rule reaches past argv: `python3 <<'PY' … PY` has an argv of one
 *    word, so the row leads with a line of the script it was handed and says how
 *    many lines that script was.
 * 3. **Never claim more than the source says.** A word that survives as a
 *    literal is shown unquoted; anything with an expansion or substitution is
 *    shown as written (`$HOME/notes`), never resolved. Control flow (loops,
 *    `if`, functions) isn't summarized at all: a script is not one action.
 *
 * Parsing is done with the same lazily loaded bash AST the file-op layer uses
 * (see {@link ./shell-parser.ts}), so quoting, pipelines, and `&&` chains are
 * the parser's problem rather than a pile of regexes over the raw string.
 */
import type sh from 'mvdan-sh';
import {
  stripTimeoutArgv,
  unwrapShellCommand,
  TRANSPARENT_PREFIXES,
} from './turn-blocks/shell-command.js';
import { parseGistSubject } from './gist-links.js';
import { getShellSyntax, redirOperator } from './shell-parser.js';
import { verbs, type VerbWords } from './verb-forms.js';

type Syntax = typeof sh.syntax;
type Node = Parameters<Syntax['NodeType']>[0];

/**
 * Semantic icon slot. A key, not a component: this module stays free of React
 * so it can be tested as pure data, and the view owns the icon set.
 */
export type ShellSummaryIcon =
  | 'search'
  | 'git'
  | 'commit'
  | 'branch'
  | 'diff'
  | 'push'
  | 'pull'
  | 'replace'
  | 'read'
  | 'folder'
  | 'file-plus'
  | 'trash'
  | 'move'
  | 'copy'
  | 'test'
  | 'build'
  | 'package'
  | 'pr'
  | 'gist'
  | 'globe'
  | 'terminal';

/**
 * The thing a step acted on. `kind` is a rendering hint, not a claim about the
 * filesystem: `path` means "show it like a path" (mono, clipped from the left),
 * `pattern` means "show it quoted", `ref` is a git ref chip, `gist` is a GitHub
 * gist chip, `code` is a line of a program the command was handed (clipped from
 * the right, where code reads from).
 */
export interface ShellSubject {
  kind: 'pattern' | 'path' | 'ref' | 'gist' | 'text' | 'code';
  text: string;
  /** Set for a two-sided action (`mv a b`, `s/old/new/`), rendered as `text → to`. */
  to?: string;
}

/**
 * Every verb a step can lead with, named once in its plain form.
 * {@link ./verb-forms.ts} builds the three tenses a row needs from it: past
 * tense for a call that finished, present participle while it runs, and what
 * to say when it failed.
 *
 * A row that says "Pushed to feat/x" over a rejected push states an outcome
 * that never happened, which is the one thing a summary must never do — so a
 * verb missing from this table is a type error, not a row that quietly claims
 * success. Most failures read "Couldn't <verb>", which stays true whether the
 * command refused, errored, or exited non-zero for a reason of its own; the few
 * where a plainer noun reads better say so with a `failed` override.
 */
const SHELL_VERBS = verbs({
  'search for': {},
  find: {},
  read: {},
  replace: {},
  list: {},
  run: {},
  'run make': {},
  fetch: {},
  call: {},

  // git
  check: {},
  diff: {},
  show: {},
  stage: {},
  commit: {},
  push: {},
  'remotely delete': { inflect: 1 },
  pull: {},
  clone: {},
  'create branch': {},
  'delete branch': {},
  'switch to': {},
  restore: {},
  merge: {},
  'rebase onto': {},
  'reset to': {},
  stash: {},
  pop: {},
  apply: {},
  drop: {},
  revert: {},
  'cherry pick': { inflect: 1 },
  tag: {},
  blame: {},

  // gh
  view: {},
  open: {},
  close: {},
  'comment on': {},
  watch: {},

  // gh gist
  'create a gist from': {},
  'view gist': {},
  'edit gist': {},
  'clone gist': {},
  'delete gist': {},
  'rename a file in gist': {},

  // package scripts
  'run tests': { failed: 'Tests failed' },
  'run unit tests': { failed: 'Unit tests failed' },
  'run end to end tests': { failed: 'End to end tests failed' },
  build: { failed: 'Build failed' },
  lint: { failed: 'Lint failed' },
  format: {},
  'type check': { inflect: 1, failed: 'Type check failed' },
  verify: { failed: 'Verification failed' },
  'start the dev server': { failed: "Dev server didn't start" },
  start: {},
  install: {},
  add: {},
  remove: {},
  update: {},

  // files
  create: {},
  'create directory': {},
  delete: {},
  'delete directory': {},
  move: {},
  copy: {},
  'change permissions on': {},
});

/** A verb this module knows all three tenses of, in its plain form: `push`. */
export type ShellVerb = keyof typeof SHELL_VERBS;

/** One command in the chain, described. */
export interface ShellStep {
  icon: ShellSummaryIcon;
  /** What the step did, in its plain form: `search for`. */
  verb: ShellVerb;
  /** {@link verb}'s words, one per tense — the row picks the one it needs. */
  words: VerbWords;
  subject?: ShellSubject;
  /** Muted qualifiers, most informative first: `in web/src`, `*.ts`. */
  details: string[];
  /** This command's exact source text, redirections included. */
  raw: string;
  /** False for a command we have no summary for; it shows its raw text. */
  recognized: boolean;
  /**
   * True when a non-zero exit code reports what the command observed rather
   * than a failure of the command itself: a search with no matches, or
   * `gh pr checks` when checks are failing. When set, the view shows the
   * completed tense — "Searched for X", "Checked pull request checks" —
   * instead of the failed tense, because the command did what it was asked.
   */
  exitReportsOutcome?: boolean;
}

export interface ShellSummary {
  /** The step the row's headline is drawn from: the first significant command. */
  headline: ShellStep;
  /** Every significant command, in source order. Always starts with `headline`. */
  steps: ShellStep[];
}

/**
 * A row summary is a glance affordance. Past a few thousand characters the
 * command is a script pasted into a shell, and the raw view is the honest one.
 */
const MAX_COMMAND_LENGTH = 20_000;

/** Subjects are clipped by CSS, but an absurd one shouldn't reach the DOM at all. */
const MAX_SUBJECT_LENGTH = 400;

/* -------------------------------------------------------------------------- */
/* AST walking                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `BinaryCmd.Op` is a Go enum the JS build exposes only as opaque numbers, so
 * recover the two we care about from the parser itself rather than hard-coding
 * integers. Same approach {@link redirOperator} takes for redirections.
 */
interface BinaryOps {
  pipe: number;
  pipeAll: number;
}

const binaryOpsCache = new WeakMap<object, BinaryOps>();

function binaryOps(syntax: Syntax): BinaryOps {
  const cached = binaryOpsCache.get(syntax);
  if (cached) return cached;
  const opOf = (probe: string) => {
    const cmd = syntax.NewParser().Parse(probe, 'probe.sh').Stmts[0]?.Cmd as { Op?: number } | null;
    // A probe that somehow didn't parse leaves an operator nothing can equal.
    return cmd?.Op ?? -1;
  };
  const ops: BinaryOps = { pipe: opOf('a | b'), pipeAll: opOf('a |& b') };
  binaryOpsCache.set(syntax, ops);
  return ops;
}

/** The redirection operators that hand a command text on stdin. */
interface StdinOps {
  heredoc: number;
  heredocDash: number;
  hereString: number;
}

function stdinOps(syntax: Syntax): StdinOps {
  return {
    heredoc: redirOperator(syntax, "x <<'E'\nE"),
    heredocDash: redirOperator(syntax, "x <<-'E'\nE"),
    hereString: redirOperator(syntax, 'x <<< y'),
  };
}

/**
 * The text a stdin redirection carries, part by part. Unlike {@link wordText}
 * this never falls back to the node's whole source range: for a heredoc that
 * range runs past the body to the closing delimiter, and for a herestring it
 * keeps the quotes bash strips. An expansion still shows as written (`$HOME`),
 * because displaying it is honest and resolving it is not something we can do.
 */
function stdinText(syntax: Syntax, source: string, node: Node): string {
  let out = '';
  for (const part of (node as { Parts?: Node[] }).Parts ?? []) {
    const type = syntax.NodeType(part);
    if (type === 'Lit' || type === 'SglQuoted') out += (part as { Value?: string }).Value ?? '';
    else if (type === 'DblQuoted') out += stdinText(syntax, source, part);
    else out += sourceText(part, source);
  }
  return out;
}

/**
 * The text a statement's redirections feed the command on stdin, or null when
 * nothing does. This is the script an agent runs with `python3 <<'PY' … PY` or
 * `node <<< 'code'`: the argv says only `python3`, so without the body there is
 * nothing on the row worth reading.
 */
function stdinCode(syntax: Syntax, source: string, stmt: Node): string | null {
  const ops = stdinOps(syntax);
  for (const redirect of (stmt as { Redirs?: Node[] }).Redirs ?? []) {
    const node = redirect as unknown as {
      Op: number;
      N?: { Value?: string } | null;
      Word: Node | null;
      Hdoc: Node | null;
    };
    // `python3 3<<'PY'` feeds file descriptor 3, which a command reads only if it
    // was written to; the script an interpreter runs always arrives on fd 0.
    const fd = node.N?.Value;
    if (fd != null && fd !== '0') continue;
    if (node.Op === ops.hereString) {
      return node.Word ? stdinText(syntax, source, node.Word) : null;
    }
    if (node.Op === ops.heredoc || node.Op === ops.heredocDash) {
      // A heredoc with an empty body carries no word at all.
      if (!node.Hdoc) return '';
      const body = stdinText(syntax, source, node.Hdoc);
      // `<<-` strips leading tabs from every line of the body, so the text here
      // is what bash actually feeds, whatever a caller goes on to do with it.
      return node.Op === ops.heredocDash ? body.replace(/^\t+/gm, '') : body;
    }
  }
  return null;
}

/** A single command found in the chain, with the text it came from. */
interface RawCommand {
  argv: string[];
  raw: string;
  /** True when this command sits on the right of a pipe, so it shapes output rather than doing work. */
  filter: boolean;
  /** Text a heredoc or herestring feeds the command on stdin, if any. */
  stdin: string | null;
  /**
   * How many redirections the statement carries. A `cat` whose only redirection
   * is its heredoc is pure text; one that also writes a file did work.
   */
  redirections: number;
}

/**
 * The text a word contributes, preferring its literal value so `"Mark as
 * blocked"` loses its quotes, and falling back to the source text when the word
 * expands (`$HOME/notes`) — displaying it as written is honest; resolving it is
 * not something we can do.
 */
function wordText(syntax: Syntax, source: string, word: Node): string {
  const parts = (word as { Parts?: Node[] }).Parts ?? [];
  let literal = '';
  for (const part of parts) {
    const node = part as { Value?: string; Dollar?: boolean; Parts?: Node[] };
    const type = syntax.NodeType(part);
    if (type === 'Lit' && !(node.Value ?? '').includes('\\')) {
      literal += node.Value ?? '';
      continue;
    }
    if ((type === 'SglQuoted' || type === 'DblQuoted') && !node.Dollar) {
      if (type === 'SglQuoted') {
        literal += node.Value ?? '';
        continue;
      }
      const inner = node.Parts ?? [];
      if (inner.every((p) => syntax.NodeType(p) === 'Lit')) {
        literal += inner.map((p) => (p as { Value?: string }).Value ?? '').join('');
        continue;
      }
    }
    return sourceText(word, source);
  }
  return literal;
}

function sourceText(node: Node, source: string): string {
  const range = node as unknown as { Pos(): { Offset(): number }; End(): { Offset(): number } };
  return source.slice(range.Pos().Offset(), range.End().Offset());
}

/**
 * Flatten a statement into the commands it runs, in source order. Returns null
 * for anything that isn't a straight chain — a loop, `if`, `case`, a function
 * definition — because a row headline can't describe control flow without
 * lying about it.
 */
function collectCommands(
  syntax: Syntax,
  source: string,
  stmt: Node,
  filter: boolean,
  out: RawCommand[],
): boolean {
  const cmd = (stmt as { Cmd?: Node | null }).Cmd ?? null;
  if (!cmd) return false; // a bare redirect (`> file`) runs nothing
  const type = syntax.NodeType(cmd);

  if (type === 'CallExpr') {
    const call = cmd as unknown as { Assigns: unknown[]; Args: Node[] };
    // `FOO=1 pnpm test` still runs pnpm; a bare `FOO=1` runs nothing.
    if (call.Args.length === 0) return true;
    out.push({
      argv: call.Args.map((word) => wordText(syntax, source, word)),
      // The statement, not the call: that keeps redirections in the step's text.
      raw: sourceText(stmt, source),
      filter,
      stdin: stdinCode(syntax, source, stmt),
      redirections: ((stmt as { Redirs?: Node[] }).Redirs ?? []).length,
    });
    return true;
  }

  if (type === 'BinaryCmd') {
    const binary = cmd as unknown as { Op: number; X: Node; Y: Node };
    const ops = binaryOps(syntax);
    const piped = binary.Op === ops.pipe || binary.Op === ops.pipeAll;
    return (
      collectCommands(syntax, source, binary.X, filter, out) &&
      // Only the right side of a pipe is a filter; `a | b && c` leaves `c` on its own.
      collectCommands(syntax, source, binary.Y, piped || filter, out)
    );
  }

  if (type === 'Subshell' || type === 'Block') {
    const group = cmd as unknown as { Stmts: Node[] };
    return group.Stmts.every((inner) => collectCommands(syntax, source, inner, filter, out));
  }

  return false;
}

/* -------------------------------------------------------------------------- */
/* Argument parsing                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Flags and operands for one command. Deliberately forgiving where
 * {@link ./shell-file-ops.ts} is strict: that module proves a command is a file
 * op, this one only describes it, so an unknown flag is skipped rather than
 * fatal.
 */
interface ParsedArgs {
  flags: string[];
  operands: string[];
  /** True for `--include` given `--include=*.ts`, and for `-r` given `-rn`. */
  has(name: string): boolean;
  /** `--include=*.ts`, `--include *.ts`, `-n 20`, and `-n20` all resolve. */
  value(name: string): string | null;
  /** The `3` in `git log -3`, a count written as a bare flag. */
  countFlag(): number | null;
}

function parseArgs(args: string[], valueFlags: ReadonlySet<string> = new Set()): ParsedArgs {
  const flags: string[] = [];
  const operands: string[] = [];
  const values = new Map<string, string>();
  let optionsEnded = false;

  for (let i = 0; i < args.length; i += 1) {
    const arg = args[i];
    if (!optionsEnded && arg === '--') {
      optionsEnded = true;
      continue;
    }
    if (optionsEnded || arg.length < 2 || !arg.startsWith('-')) {
      operands.push(arg);
      continue;
    }
    flags.push(arg);
    const equals = arg.indexOf('=');
    if (arg.startsWith('--') && equals > 0) {
      values.set(arg.slice(0, equals), arg.slice(equals + 1));
      continue;
    }
    if (valueFlags.has(arg)) {
      const next = args[i + 1];
      if (next !== undefined) {
        values.set(arg, next);
        i += 1;
      }
      continue;
    }
    // An attached short value: `-n20`, `-m5`.
    const short = arg.slice(0, 2);
    if (!arg.startsWith('--') && arg.length > 2 && valueFlags.has(short)) {
      values.set(short, arg.slice(2));
    }
  }

  return {
    flags,
    operands,
    has(name) {
      if (values.has(name) || flags.includes(name)) return true;
      // A short flag can arrive bundled: `-rn` carries both `-r` and `-n`.
      if (name.startsWith('--') || name.length !== 2) return false;
      const letter = name[1];
      return flags.some(
        (flag) => !flag.startsWith('--') && !/^-\d+$/.test(flag) && flag.slice(1).includes(letter),
      );
    },
    value: (name) => values.get(name) ?? null,
    countFlag() {
      for (const flag of flags) {
        const match = /^-(\d+)$/.exec(flag);
        if (match) return Number(match[1]);
      }
      return null;
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Step helpers                                                                */
/* -------------------------------------------------------------------------- */

/**
 * What a recognizer returns: a step without the bookkeeping fields, and naming
 * only the verb — {@link SHELL_VERBS} owns the words for each tense.
 */
type StepDraft = Omit<ShellStep, 'raw' | 'recognized' | 'details' | 'words'> & {
  details?: string[];
};

/**
 * `stdin` is the text the command was handed on stdin (a heredoc body, a
 * herestring, or a piped `echo`), which for a script runner *is* the command's
 * subject: `python3 <<'PY'` says nothing about what ran.
 */
type Recognizer = (args: ParsedArgs, argv: string[], stdin: string | null) => StepDraft | null;

function clip(text: string): string {
  return text.length > MAX_SUBJECT_LENGTH ? text.slice(0, MAX_SUBJECT_LENGTH) + '…' : text;
}

function pattern(text: string): ShellSubject {
  return { kind: 'pattern', text: clip(text) };
}

function path(text: string, to?: string): ShellSubject {
  return { kind: 'path', text: clip(text), ...(to ? { to: clip(to) } : {}) };
}

function ref(text: string): ShellSubject {
  return { kind: 'ref', text: clip(text) };
}

function gist(text: string): ShellSubject {
  return { kind: 'gist', text: clip(text) };
}

function text(value: string): ShellSubject {
  return { kind: 'text', text: clip(value) };
}

function code(value: string): ShellSubject {
  return { kind: 'code', text: clip(value) };
}

/** `in web/src` for one path, a count once naming them all would fill the row. */
function inPaths(paths: string[]): string[] {
  const named = paths.filter((p) => p !== '.' && p !== './');
  if (named.length === 0) return [];
  if (named.length === 1) return [`in ${named[0]}`];
  if (named.length === 2) return [`in ${named[0]} and ${named[1]}`];
  return [`in ${named.length} paths`];
}

/** `a.ts`, `a.ts and b.ts`, `4 files` — the same shape for a list of operands. */
function joinPaths(paths: string[]): string {
  if (paths.length === 1) return paths[0];
  if (paths.length === 2) return `${paths[0]} and ${paths[1]}`;
  return `${paths.length} paths`;
}

/* -------------------------------------------------------------------------- */
/* Recognizers                                                                 */
/* -------------------------------------------------------------------------- */

const GREP_VALUE_FLAGS = new Set([
  '-e',
  '-f',
  '-m',
  '-A',
  '-B',
  '-C',
  '-d',
  '-g',
  '-t',
  '--include',
  '--exclude',
  '--exclude-dir',
  '--glob',
  '--type',
  '--max-count',
  '--regexp',
]);

/** `grep`, `rg`, and friends: the pattern is the row's subject, the rest is context. */
const grepStep: Recognizer = (args) => {
  const explicit = args.value('-e') ?? args.value('--regexp');
  const query = explicit ?? args.operands[0];
  if (!query) return null;
  const paths = explicit ? args.operands : args.operands.slice(1);
  const glob =
    args.value('--include') ??
    args.value('--glob') ??
    args.value('-g') ??
    args.value('--type') ??
    args.value('-t');
  const details = [...inPaths(paths)];
  if (glob) details.push(glob);
  if (args.has('-i')) details.push('case insensitive');
  if (args.has('-l')) details.push('file names only');
  if (args.has('-c')) details.push('counts only');
  return {
    icon: 'search',
    verb: 'search for',
    subject: pattern(query),
    details,
    exitReportsOutcome: true,
  };
};

/** `s/old/new/g` in any of its separators, or null when it isn't a substitution. */
function parseSubstitution(script: string): { from: string; to: string; all: boolean } | null {
  const separator = script[1];
  if (script[0] !== 's' || !separator || /[\s\w\\]/.test(separator)) return null;
  const parts: string[] = [];
  let current = '';
  for (let i = 2; i < script.length; i += 1) {
    const ch = script[i];
    if (ch === '\\' && i + 1 < script.length) {
      current += ch + script[i + 1];
      i += 1;
      continue;
    }
    if (ch === separator) {
      parts.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  parts.push(current);
  if (parts.length !== 3) return null;
  return { from: parts[0], to: parts[1], all: parts[2].includes('g') };
}

const SED_RANGE = /^(\d+),(\d+|\$)p$/;

/**
 * Only the two sed forms whose syntax hides what they do: an in-place
 * substitution, and a ranged print. Everything else keeps its raw text, since
 * naming a sed script we haven't read would be a guess.
 */
const sedStep: Recognizer = (args) => {
  // `sed -i '' 's/a/b/' f` is the BSD spelling; the empty operand is the (absent)
  // backup suffix, not a file.
  const operands = args.operands.filter((operand) => operand.length > 0);
  const script = args.value('-e') ?? operands[0];
  if (!script) return null;
  const files = args.value('-e') ? operands : operands.slice(1);

  const substitution = parseSubstitution(script);
  if (substitution && args.has('-i')) {
    return {
      icon: 'replace',
      verb: 'replace',
      subject: { kind: 'pattern', text: clip(substitution.from), to: clip(substitution.to) },
      details: [...inPaths(files), ...(substitution.all ? ['every match'] : [])],
    };
  }

  const range = SED_RANGE.exec(script);
  if (range && args.has('-n') && files.length === 1) {
    return {
      icon: 'read',
      verb: 'read',
      subject: path(files[0]),
      details: [`lines ${range[1]} to ${range[2] === '$' ? 'end' : range[2]}`],
    };
  }
  return null;
};

const GIT_VALUE_FLAGS = new Set(['-C', '-c', '-m', '--message', '-n', '--max-count', '-b', '-B']);

/** `git commit -m "…"` and the rest: one entry per subcommand worth naming. */
const GIT_SUBCOMMANDS: Record<string, (args: ParsedArgs, rest: string[]) => StepDraft> = {
  status: () => ({
    icon: 'git',
    verb: 'check',
    subject: text('git status'),
  }),
  log: (args, rest) => {
    const count = args.value('-n') ?? args.value('--max-count') ?? args.countFlag()?.toString();
    return {
      icon: 'git',
      verb: 'read',
      subject: text('git log'),
      details: [
        ...(count ? [`${count} ${count === '1' ? 'commit' : 'commits'}`] : []),
        ...inPaths(rest),
      ],
    };
  },
  diff: (args, rest) => ({
    icon: 'diff',
    verb: 'diff',
    subject: rest.length > 0 ? ref(joinPaths(rest)) : text('the working tree'),
    details: [
      ...(args.has('--staged') || args.has('--cached') ? ['staged'] : []),
      ...(args.has('--stat') ? ['summary'] : []),
      ...(args.has('--name-only') ? ['file names only'] : []),
    ],
  }),
  show: (_args, rest) => ({
    icon: 'git',
    verb: 'show',
    subject: ref(rest[0] ?? 'HEAD'),
  }),
  add: (args, rest) => ({
    icon: 'commit',
    verb: 'stage',
    subject:
      args.has('-A') || args.has('--all') || rest[0] === '.' || rest.length === 0
        ? text('every change')
        : path(joinPaths(rest)),
  }),
  commit: (args) => {
    const message = args.value('-m') ?? args.value('--message');
    return {
      icon: 'commit',
      verb: 'commit',
      subject: message ? text(message) : text('staged changes'),
      details: [
        ...(args.has('--amend') ? ['amended'] : []),
        ...(args.has('--no-verify') ? ['hooks skipped'] : []),
      ],
    };
  },
  push: (args, rest) => {
    if (args.has('--delete') || args.has('-d')) {
      return {
        icon: 'push',
        verb: 'remotely delete',
        subject: rest.length > 0 ? ref(rest.join(' ')) : text('the remote branch'),
      };
    }
    return {
      icon: 'push',
      verb: 'push',
      subject: rest.length > 0 ? ref(`to ${rest.join(' ')}`) : text('the current branch'),
      details: [
        ...(args.has('--force') || args.has('-f') || args.has('--force-with-lease')
          ? ['forced']
          : []),
        ...(args.has('-u') || args.has('--set-upstream') ? ['tracking'] : []),
      ],
    };
  },
  pull: (_args, rest) => ({
    icon: 'pull',
    verb: 'pull',
    subject: rest.length > 0 ? ref(rest.join(' ')) : text('the current branch'),
  }),
  fetch: (_args, rest) => ({
    icon: 'pull',
    verb: 'fetch',
    subject: ref(rest.length > 0 ? rest.join(' ') : 'origin'),
  }),
  clone: (_args, rest) => ({
    icon: 'pull',
    verb: 'clone',
    subject: text(rest[0] ?? 'a repository'),
  }),
  checkout: (args, rest) => {
    const created = args.value('-b') ?? args.value('-B');
    if (created) {
      return {
        icon: 'branch',
        verb: 'create branch',
        subject: ref(created),
      };
    }
    if (args.has('--')) {
      return {
        icon: 'git',
        verb: 'restore',
        subject: path(rest.join(' ')),
      };
    }
    return {
      icon: 'branch',
      verb: 'switch to',
      subject: ref(rest[0] ?? 'HEAD'),
    };
  },
  switch: (args, rest) => ({
    icon: 'branch',
    verb: args.has('-c') ? 'create branch' : 'switch to',
    subject: ref(args.value('-c') ?? rest[0] ?? 'HEAD'),
  }),
  branch: (args, rest) => {
    if (args.has('-d') || args.has('-D')) {
      return {
        icon: 'branch',
        verb: 'delete branch',
        subject: ref(joinPaths(rest)),
      };
    }
    if (rest.length > 0) {
      return {
        icon: 'branch',
        verb: 'create branch',
        subject: ref(rest[0]),
      };
    }
    return { icon: 'branch', verb: 'list', subject: text('branches') };
  },
  merge: (_args, rest) => ({
    icon: 'branch',
    verb: 'merge',
    subject: ref(rest[0] ?? 'a branch'),
  }),
  rebase: (args, rest) => ({
    icon: 'branch',
    verb: 'rebase onto',
    subject: ref(rest[0] ?? 'the upstream branch'),
    details: args.has('--continue') ? ['continued'] : args.has('--abort') ? ['aborted'] : [],
  }),
  reset: (args, rest) => ({
    icon: 'git',
    verb: 'reset to',
    subject: ref(rest[0] ?? 'HEAD'),
    // `--hard` discards work, which is the whole story of the row.
    details: args.has('--hard') ? ['hard'] : args.has('--soft') ? ['soft'] : [],
  }),
  restore: (args, rest) => ({
    icon: 'git',
    verb: 'restore',
    subject: path(joinPaths(rest)),
    details: args.has('--staged') ? ['unstaged'] : [],
  }),
  stash: (_args, rest) => {
    const action = rest[0];
    const verb: ShellVerb =
      action === 'pop'
        ? 'pop'
        : action === 'apply'
          ? 'apply'
          : action === 'drop'
            ? 'drop'
            : action === 'list'
              ? 'list'
              : 'stash';
    return {
      icon: 'git',
      verb,
      subject: text(action && action !== 'push' ? 'the stash' : 'the working tree'),
    };
  },
  revert: (_args, rest) => ({
    icon: 'git',
    verb: 'revert',
    subject: ref(rest[0] ?? 'HEAD'),
  }),
  'cherry-pick': (_args, rest) => ({
    icon: 'git',
    verb: 'cherry pick',
    subject: ref(rest[0] ?? 'a commit'),
  }),
  tag: (_args, rest) => ({
    icon: 'git',
    verb: rest.length > 0 ? 'tag' : 'list',
    subject: rest.length > 0 ? ref(rest[0]) : text('tags'),
  }),
  blame: (_args, rest) => ({
    icon: 'git',
    verb: 'blame',
    subject: path(rest[0] ?? ''),
  }),
};

const gitStep: Recognizer = (args, argv) => {
  const subcommand = args.operands[0];
  if (!subcommand) return null;
  const rest = args.operands.slice(1);

  // `git grep` is a grep, and reads better as one.
  if (subcommand === 'grep') {
    return grepStep(parseArgs(argv.slice(2), GREP_VALUE_FLAGS), argv.slice(1), null);
  }

  const handler = GIT_SUBCOMMANDS[subcommand];
  if (handler) return handler(args, rest);
  // Every other subcommand still reads better as "Ran git bisect" than as a
  // clipped command, and the raw text is one hover away either way.
  return {
    icon: 'git',
    verb: 'run',
    subject: text(`git ${subcommand}`),
  };
};

const GH_SUBJECTS: Record<string, string> = {
  pr: 'pull requests',
  issue: 'issues',
  run: 'workflow runs',
  release: 'releases',
  repo: 'repositories',
  workflow: 'workflows',
};

/**
 * `gh gist <action>`, where the action acts on one gist. The id is the row's
 * subject rather than a word inside it, so the view can chip it and open it
 * into the gist itself ({@link ./gist-links.ts}) — the same move the git
 * recognizers make with a ref.
 */
const GIST_FORMS: Record<string, ShellVerb> = {
  view: 'view gist',
  edit: 'edit gist',
  clone: 'clone gist',
  delete: 'delete gist',
  rename: 'rename a file in gist',
};

/**
 * Flags `gh gist` reads a value from. They are the subcommand's own, not `gh`'s:
 * `-f` is the gist's filename here and `gh pr create`'s boolean `--fill`
 * elsewhere, so reading it everywhere would swallow the flag after it. Hence the
 * re-parse below, the same move {@link gitStep} makes for `git grep`.
 */
const GIST_VALUE_FLAGS = new Set([
  '-f',
  '--filename',
  '-d',
  '--desc',
  '-a',
  '--add',
  '-r',
  '--remove',
  '-L',
  '--limit',
]);

/**
 * `gh gist …`. A gist is not a repository entity, so the generic `gh` shape
 * reads wrong for it ("Opened a gist" for a `create`); the actions that name one
 * hand their id to the chip, and the ones that name none say so in words.
 * Returns null for anything else so the caller's generic handling still applies.
 */
function ghGistStep(argv: string[]): StepDraft | null {
  // `argv` is the whole command, so the gist's own operands start after
  // `gh gist <action>`.
  const args = parseArgs(argv.slice(1), GIST_VALUE_FLAGS);
  const [, action, ...rest] = args.operands;
  const visibility = args.has('--public') ? ['public'] : args.has('--secret') ? ['secret'] : [];
  if (action === 'list') {
    return { icon: 'gist', verb: 'list', subject: text('gists') };
  }
  if (action === 'create') {
    // `-` is the conventional spelling of "the content is on stdin", not a file.
    const files = rest.filter((operand) => operand !== '-');
    return {
      icon: 'gist',
      verb: files.length > 0 ? 'create a gist from' : 'create',
      subject: files.length > 0 ? path(joinPaths(files)) : text('a gist'),
      details: visibility,
    };
  }
  const form = action ? GIST_FORMS[action] : undefined;
  if (!form || !rest[0]) return null;
  const filename = args.value('-f') ?? args.value('--filename');
  return {
    icon: 'gist',
    verb: form,
    subject: gist(rest[0]),
    details: [...(filename ? [filename] : []), ...visibility],
  };
}

const ghStep: Recognizer = (args, argv) => {
  const [group, action, ...rest] = args.operands;
  if (!group) return null;
  if (group === 'api') {
    return {
      icon: 'globe',
      verb: 'call',
      subject: text(`the GitHub API${action ? ` (${action})` : ''}`),
    };
  }
  if (group === 'gist') {
    const step = ghGistStep(argv);
    if (step) return step;
  }
  // A gist group that reached here is one `ghGistStep` had nothing better for
  // (`gh gist view` with no id, `gh gist archive`); it still reads as a gist.
  const icon: ShellSummaryIcon = group === 'gist' ? 'gist' : 'pr';
  const noun = GH_SUBJECTS[group] ?? group;
  const singular = noun.replace(/s$/, '');
  const target = rest[0] ? ` ${rest[0]}` : '';
  const forms: Record<string, [ShellVerb, string]> = {
    list: ['list', noun],
    view: ['view', `${singular}${target}`],
    create: ['open', `a ${singular}`],
    checks: ['check', `${singular} checks`],
    merge: ['merge', `${singular}${target}`],
    close: ['close', `${singular}${target}`],
    diff: ['diff', `${singular}${target}`],
    comment: ['comment on', `${singular}${target}`],
    watch: ['watch', noun],
  };
  const form = action ? forms[action] : undefined;
  if (!form) {
    return {
      icon,
      verb: 'run',
      subject: text(`gh ${group}${action ? ` ${action}` : ''}`),
    };
  }
  // `checks` and `watch` exit non-zero when the thing they observed failed,
  // not when the command itself did: `gh pr checks` returns 1 when CI checks
  // are red, and `gh run watch` returns 1 when the watched run fails.
  const exitReportsOutcome = action === 'checks' || action === 'watch';
  return {
    icon,
    verb: form[0],
    subject: text(form[1]),
    details: args.value('--head') ? [`head ${args.value('--head')}`] : [],
    ...(exitReportsOutcome && { exitReportsOutcome }),
  };
};

/** Package scripts everyone runs, mapped to what they actually do. */
const SCRIPT_VERBS: Record<string, [ShellVerb, ShellSummaryIcon]> = {
  test: ['run tests', 'test'],
  'test:unit': ['run unit tests', 'test'],
  'test:e2e': ['run end to end tests', 'test'],
  build: ['build', 'build'],
  lint: ['lint', 'build'],
  format: ['format', 'build'],
  typecheck: ['type check', 'build'],
  verify: ['verify', 'build'],
  dev: ['start the dev server', 'terminal'],
  start: ['start', 'terminal'],
};

const PM_VALUE_FLAGS = new Set(['-F', '--filter', '-C', '--dir', '-w', '--workspace']);
const PM_INSTALLS: Record<string, ShellVerb> = {
  install: 'install',
  i: 'install',
  add: 'add',
  remove: 'remove',
  uninstall: 'remove',
  update: 'update',
  up: 'update',
};

const packageManagerStep: Recognizer = (args, argv, stdin) => {
  const manager = argv[0];

  // Some package managers (e.g. bun) also accept a code flag (-e/--eval) to run
  // inline scripts. PM_VALUE_FLAGS does not include those flags, so they are not
  // consumed as value flags and the code ends up as the first operand — a
  // misidentified subcommand. Re-parse with the runner's code flags and delegate
  // to scriptRunnerStep when one of them is present.
  const codeFlags = RUNNER_CODE_FLAGS[binaryName(manager)];
  if (codeFlags && codeFlags.length > 0) {
    const codeArgs = parseArgs(argv.slice(1), new Set(codeFlags));
    const inline = codeFlags.map((f) => codeArgs.value(f)).find((v) => v != null);
    if (inline != null) return scriptRunnerStep(codeArgs, argv, stdin);
  }

  const [first, ...rest] = args.operands;
  if (!first) return null;
  const filter = args.value('-F') ?? args.value('--filter');
  const scope = filter ? [`in ${filter}`] : [];

  // `pnpm exec vitest run` / `npx tsx script.ts`: the interesting command is the
  // inner one, so describe that instead of the runner. `npx` runs its first
  // operand; `pnpm exec` runs whatever follows the `exec`.
  const delegated = first === 'exec' || first === 'dlx' || first === 'run-script';
  if (delegated || manager === 'npx' || manager === 'pnpx') {
    const inner = recognize(argv.slice(argv.indexOf(first) + (delegated ? 1 : 0)), stdin);
    if (inner) return { ...inner, details: [...(inner.details ?? []), ...scope] };
  }

  const install = PM_INSTALLS[first];
  if (install) {
    return {
      icon: 'package',
      verb: install,
      subject: rest.length > 0 ? text(joinPaths(rest)) : text('dependencies'),
      details: scope,
    };
  }

  const script = first === 'run' ? rest[0] : first;
  if (!script) return null;
  const known = SCRIPT_VERBS[script];
  if (known) {
    return { icon: known[1], verb: known[0], details: scope };
  }
  return {
    icon: 'terminal',
    verb: 'run',
    subject: text(`${manager} ${script}`),
    details: scope,
  };
};

const TEST_RUNNERS = new Set(['vitest', 'jest', 'pytest', 'playwright', 'mocha', 'ava']);

const testRunnerStep: Recognizer = (args, argv) => ({
  icon: 'test',
  verb: 'run tests',
  subject: args.operands.filter((operand) => operand !== 'run').length
    ? path(joinPaths(args.operands.filter((operand) => operand !== 'run')))
    : undefined,
  details: [argv[0]],
});

const FILE_VALUE_FLAGS = new Set(['--mode']);

const FILE_COMMANDS: Record<string, Recognizer> = {
  ls: (args) => ({
    icon: 'folder',
    verb: 'list',
    subject: path(args.operands.length > 0 ? joinPaths(args.operands) : '.'),
  }),
  mkdir: (args) => ({
    icon: 'folder',
    verb: 'create directory',
    subject: path(joinPaths(args.operands)),
  }),
  touch: (args) => ({
    icon: 'file-plus',
    verb: 'create',
    subject: path(joinPaths(args.operands)),
  }),
  rm: (args) => ({
    icon: 'trash',
    verb: 'delete',
    subject: path(joinPaths(args.operands)),
    details: args.has('-r') || args.has('-R') ? ['recursive'] : [],
  }),
  rmdir: (args) => ({
    icon: 'trash',
    verb: 'delete directory',
    subject: path(joinPaths(args.operands)),
  }),
  mv: (args) => {
    const { operands } = args;
    if (operands.length !== 2) return null;
    return {
      icon: 'move',
      verb: 'move',
      subject: path(operands[0], operands[1]),
    };
  },
  cp: (args) => {
    const { operands } = args;
    if (operands.length !== 2) return null;
    return {
      icon: 'copy',
      verb: 'copy',
      subject: path(operands[0], operands[1]),
      details: args.has('-r') || args.has('-R') ? ['recursive'] : [],
    };
  },
  chmod: (args) => {
    const [mode, ...paths] = args.operands;
    if (!mode || paths.length === 0) return null;
    return {
      icon: 'file-plus',
      verb: 'change permissions on',
      subject: path(joinPaths(paths)),
      details: [mode],
    };
  },
};

const FIND_VALUE_FLAGS = new Set(['-name', '-iname', '-path', '-type', '-maxdepth', '-newer']);

const findStep: Recognizer = (args) => {
  // `find … -delete` / `-exec rm {} \;` deletes what it matches. Calling that
  // "Found" would describe a search and hide a deletion, so it keeps its raw
  // text rather than getting a headline that reads as harmless.
  if (args.has('-delete') || args.has('-exec') || args.has('-execdir') || args.has('-ok')) {
    return null;
  }
  const name = args.value('-name') ?? args.value('-iname') ?? args.value('-path');
  const type = args.value('-type');
  return {
    icon: 'search',
    verb: 'find',
    subject: name ? pattern(name) : text(type === 'd' ? 'directories' : 'files'),
    details: [...inPaths(args.operands), ...(name && type === 'd' ? ['directories'] : [])],
  };
};

const FETCH_VALUE_FLAGS = new Set(['-X', '-H', '-d', '--data', '--header', '-o', '--output']);

const fetchStep: Recognizer = (args) => {
  const url = args.operands.find((operand) => /^[a-z]+:\/\//i.test(operand)) ?? args.operands[0];
  if (!url) return null;
  const method = args.value('-X');
  // A gist fetched over HTTP is the same gist `gh gist view` names, so the row
  // offers the same chip — the URL still reads as written, and the id behind it
  // is one click away.
  const isGist = parseGistSubject(url) !== null;
  return {
    icon: isGist ? 'gist' : 'globe',
    verb: 'fetch',
    subject: isGist ? gist(url) : text(url),
    details: method ? [method] : [],
  };
};

const SCRIPT_RUNNERS = new Set(['node', 'tsx', 'ts-node', 'python', 'python3', 'deno', 'bun']);

/**
 * Flags whose value is a program rather than a path, per runner: the same letter
 * means different things to different interpreters. `-c` is python's inline
 * script but deno's config file, and reading node's `-e` everywhere would make
 * `python3 -m pip install -e .` headline as the code `.`.
 */
const RUNNER_CODE_FLAGS: Record<string, readonly string[]> = {
  node: ['-e', '-p', '--eval', '--print'],
  bun: ['-e', '--eval'],
  tsx: ['-e', '--eval'],
  'ts-node': ['-e', '--eval', '-p', '--print'],
  python: ['-c'],
  python3: ['-c'],
  deno: [],
};

/**
 * Lines that say nothing about what a script does: shebangs, comments, and the
 * imports every script opens with. Skipped when picking the line to lead with,
 * so a heredoc doesn't headline as `import json`.
 */
const BORING_CODE_LINE =
  /^(?:#|\/\/|\/\*|\*|"""|'''|import\b|from\s+\S+\s+import\b|(?:const|let|var)\s.*\brequire\(|require\()/;

/**
 * The one line of a program that best says what it does, plus how many lines it
 * actually was. The row can only hold one line, so the count is what keeps the
 * excerpt from reading as the whole script.
 */
function codeGist(source: string): { line: string; lines: number } | null {
  const lines = source
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  // A shebang only names the interpreter, which the details chip already does,
  // so it can't be the line the row leads with — not even as the last resort.
  const body = lines.filter((line) => !line.startsWith('#!'));
  if (body.length === 0) return null;
  const meaningful = body.find((line) => !BORING_CODE_LINE.test(line));
  return { line: meaningful ?? body[0], lines: lines.length };
}

/**
 * `node`, `python3`, and friends. The interesting part is the program, which
 * arrives one of three ways: as a file, as a `-c`/`-e` payload, or on stdin from
 * a heredoc, a herestring, or a pipe. A stdin script leaves an argv of just
 * `python3` (or `python3 -`, where `-` is stdin, not a file), so a row built
 * from argv alone reads as `Ran -` — the code itself is the only thing on the
 * row worth reading.
 */
const scriptRunnerStep: Recognizer = (args, argv, stdin) => {
  const runner = binaryName(argv[0]);
  // `-` is the conventional spelling of "the script is on stdin", not a file.
  // Any operand after `-` is a script argument, not a file path — discard all
  // operands when `-` appears so positional args are not mistaken for a file.
  const stdinDash = args.operands.includes('-');
  const script = stdinDash ? undefined : args.operands.find((operand) => operand !== '-');
  const inline = (RUNNER_CODE_FLAGS[runner] ?? [])
    .map((flag) => args.value(flag))
    .find((value) => value != null);
  const program = inline ?? (script ? null : stdin);

  if (program != null) {
    const excerpt = codeGist(program);
    if (excerpt) {
      return {
        icon: 'terminal',
        verb: 'run',
        subject: code(excerpt.line),
        details: [runner, ...(excerpt.lines > 1 ? [`${excerpt.lines} lines`] : [])],
      };
    }
  }
  if (!script) return null;
  return {
    icon: 'terminal',
    verb: 'run',
    subject: path(script),
    details: [runner],
  };
};

const RECOGNIZERS: Record<string, { valueFlags?: ReadonlySet<string>; run: Recognizer }> = {
  grep: { valueFlags: GREP_VALUE_FLAGS, run: grepStep },
  egrep: { valueFlags: GREP_VALUE_FLAGS, run: grepStep },
  fgrep: { valueFlags: GREP_VALUE_FLAGS, run: grepStep },
  rg: { valueFlags: GREP_VALUE_FLAGS, run: grepStep },
  ag: { valueFlags: GREP_VALUE_FLAGS, run: grepStep },
  ack: { valueFlags: GREP_VALUE_FLAGS, run: grepStep },
  sed: { valueFlags: new Set(['-e', '-f']), run: sedStep },
  git: { valueFlags: GIT_VALUE_FLAGS, run: gitStep },
  gh: {
    valueFlags: new Set(['--head', '--base', '--title', '--body', '-R', '--repo']),
    run: ghStep,
  },
  find: { valueFlags: FIND_VALUE_FLAGS, run: findStep },
  curl: { valueFlags: FETCH_VALUE_FLAGS, run: fetchStep },
  wget: { valueFlags: FETCH_VALUE_FLAGS, run: fetchStep },
  make: {
    run: (args) => ({
      icon: 'build',
      verb: 'run make',
      subject: args.operands[0] ? text(args.operands[0]) : undefined,
    }),
  },
};

for (const manager of ['pnpm', 'npm', 'yarn', 'bun', 'npx', 'pnpx']) {
  RECOGNIZERS[manager] = { valueFlags: PM_VALUE_FLAGS, run: packageManagerStep };
}
for (const runner of TEST_RUNNERS) {
  RECOGNIZERS[runner] = { run: testRunnerStep };
}
for (const runner of SCRIPT_RUNNERS) {
  if (!RECOGNIZERS[runner]) {
    RECOGNIZERS[runner] = {
      valueFlags: new Set(RUNNER_CODE_FLAGS[runner] ?? []),
      run: scriptRunnerStep,
    };
  }
}
for (const [name, run] of Object.entries(FILE_COMMANDS)) {
  RECOGNIZERS[name] = { valueFlags: FILE_VALUE_FLAGS, run };
}

/** Strip a leading path and any `.exe`, so `/usr/bin/git` is still `git`. */
function binaryName(argv0: string): string {
  return (argv0.split('/').pop() ?? argv0).replace(/\.exe$/, '');
}

function recognize(argv: string[], stdin: string | null = null): StepDraft | null {
  const name = binaryName(argv[0] ?? '');
  const entry = RECOGNIZERS[name];
  if (!entry) return null;
  return entry.run(parseArgs(argv.slice(1), entry.valueFlags), argv, stdin);
}

/* -------------------------------------------------------------------------- */
/* Chain assembly                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Commands that carry no information about what the row did: shell bookkeeping
 * and the `echo` separators agents print between chained commands. Dropped from
 * the step list so a three-command row doesn't read as six.
 */
const NOISE = new Set([
  'echo',
  'printf',
  'true',
  'false',
  ':',
  'set',
  'export',
  'unset',
  'source',
  '.',
  'exit',
  'clear',
  'pwd',
  'sleep',
  'wait',
]);

/**
 * The text an `echo` prints, or null when we can't say it exactly. `-n` only
 * drops the trailing newline, but `-e` makes echo interpret backslash escapes —
 * so `echo -e 'a\nb'` prints two lines, not the one its argument shows — and an
 * unknown flag is unknown territory. Only leading words are flags; after the
 * first operand bash prints a `-e` verbatim.
 */
function echoText(args: string[]): string | null {
  const words: string[] = [];
  for (const arg of args) {
    if (words.length === 0) {
      if (arg === '-n') continue;
      if (arg.startsWith('-') && arg !== '-') return null;
    }
    words.push(arg);
  }
  return words.join(' ');
}

/**
 * Peel every transparent prefix off an argv, so the command is described by
 * what it does rather than by the wrapper it was launched under. `timeout`
 * belongs to that set too, but unlike `sudo` it swallows options and a duration
 * ({@link stripTimeoutArgv}) — without which `timeout 240 git fetch` would
 * summarize as an unrecognized `timeout` and leave the whole row raw.
 */
function stripPrefixes(argv: string[]): string[] {
  let rest = argv;
  for (;;) {
    if (rest[0] && TRANSPARENT_PREFIXES.has(binaryName(rest[0]))) {
      rest = rest.slice(1);
      continue;
    }
    const untimed = stripTimeoutArgv(rest);
    if (!untimed) return rest;
    rest = untimed;
  }
}

/**
 * Commands whose only job is to shape the text flowing through a pipe. Behind a
 * pipe they are dropped (or folded into a qualifier) rather than counted as
 * work. Anything not on this list keeps its own step even behind a pipe: an
 * `xargs sed -i` is not a formatting detail, and a chain that silently swallowed
 * it would read as a search that changed nothing.
 */
const OUTPUT_FILTERS = new Set([
  'head',
  'tail',
  'wc',
  'sort',
  'uniq',
  'cut',
  'tr',
  'nl',
  'rev',
  'fold',
  'column',
  'jq',
  'less',
  'more',
  'awk',
  'cat',
]);

/** Pipeline filters, described as a qualifier on the command that fed them. */
function filterDetail(argv: string[]): string | null {
  const name = binaryName(argv[0] ?? '');
  const args = parseArgs(argv.slice(1), new Set(['-n', '-c']));
  if (name === 'head') {
    const count = args.value('-n') ?? args.countFlag()?.toString() ?? '10';
    return `first ${count} lines`;
  }
  if (name === 'tail') {
    if (args.has('-f')) return 'followed';
    const count = args.value('-n') ?? args.countFlag()?.toString() ?? '10';
    return `last ${count} lines`;
  }
  if (name === 'wc') return args.has('-l') ? 'line count' : 'counted';
  if (name === 'sort') return 'sorted';
  if (name === 'uniq') return 'deduplicated';
  return null;
}

/** Fill in the words for the verb a recognizer named, and the bookkeeping fields. */
function finishStep(draft: StepDraft, raw: string, recognized: boolean): ShellStep {
  return {
    ...draft,
    words: SHELL_VERBS[draft.verb],
    details: draft.details ?? [],
    raw,
    recognized,
  };
}

function toStep(command: RawCommand): ShellStep {
  const draft = recognize(command.argv, command.stdin);
  if (!draft) {
    return finishStep(
      { icon: 'terminal', verb: 'run', subject: text(command.raw) },
      command.raw,
      false,
    );
  }
  return finishStep(draft, command.raw, true);
}

/**
 * Describe a shell command, or return null to leave the row with its plain
 * rendering — because the command doesn't parse, isn't a straight chain, or
 * starts with something we have no better words for.
 */
export function summarizeShellCommand(
  command: string,
  syntax = getShellSyntax(),
): ShellSummary | null {
  if (!syntax || command.length === 0 || command.length > MAX_COMMAND_LENGTH) return null;
  try {
    return summarize(syntax, command);
  } catch {
    // A syntax error is not a summary, and no shape the parser hands back
    // should be able to fail a chat row.
    return null;
  }
}

function summarize(syntax: Syntax, command: string): ShellSummary | null {
  const file = syntax.NewParser(syntax.Variant(syntax.LangBash)).Parse(command, 'command.sh');
  const commands: RawCommand[] = [];
  for (const stmt of file.Stmts) {
    // A backgrounded statement (`&`) is still one command; control flow is not.
    if (!collectCommands(syntax, command, stmt as Node, false, commands)) return null;
  }
  if (commands.length === 0) return null;

  const steps: ShellStep[] = [];
  /** `cd web && pnpm test` really ran in `web`, which the headline should say. */
  let workingDir: string | null = null;
  /**
   * Text a dropped command printed for the next one in the pipe to read, so
   * `echo 'print(1)' | python3` and `cat <<'PY' … PY | python3` describe the
   * program rather than the interpreter that read it.
   */
  let pipedText: string | null = null;

  for (const raw of commands) {
    const argv = stripPrefixes(raw.argv);
    if (argv.length === 0) continue; // a bare `sudo`, which runs nothing
    const name = binaryName(argv[0]);

    if (name === 'cd' || name === 'pushd') {
      if (steps.length === 0 && argv[1]) workingDir = argv[1];
      continue;
    }
    // A `cat` fed only by a heredoc adds nothing of its own: it is the heredoc,
    // spelled with an extra process. `cat -` with a heredoc is the same — `-`
    // just names stdin explicitly. A second redirection means it did something
    // with that text (`cat <<'EOF' > file` writes a file), so it stays a step.
    const catTransparent =
      name === 'cat' &&
      (argv.length === 1 || (argv.length === 2 && argv[1] === '-')) &&
      raw.stdin !== null &&
      raw.redirections === 1;
    if (catTransparent) {
      pipedText = raw.stdin;
      continue;
    }
    if (NOISE.has(name)) {
      // Only `echo` reproduces its arguments verbatim; `printf` interprets a
      // format string, so what it prints is not what the row would show.
      pipedText = name === 'echo' ? echoText(argv.slice(1)) : null;
      continue;
    }

    const stdin = raw.stdin ?? (raw.filter ? pipedText : null);
    pipedText = null;

    if (raw.filter) {
      const detail = filterDetail(argv);
      const previous = steps.at(-1);
      // A filter qualifies the command that fed it; with nothing to qualify it
      // stands on its own so the chain isn't silently dropped.
      if (previous && detail) {
        previous.details.push(detail);
        continue;
      }
      // A filter with nothing to say is dropped, but only if shaping output is
      // all it does; anything else earns its own step.
      if (previous && OUTPUT_FILTERS.has(name) && !recognize(argv, stdin)) continue;
    }

    steps.push(toStep({ ...raw, argv, stdin }));
  }

  const headline = steps[0];
  // Leading with the second command would describe the row as something it did
  // not start by doing, so an unrecognized opener keeps the whole row raw.
  if (!headline?.recognized) return null;
  if (workingDir) headline.details.push(`in ${workingDir}`);
  return { headline, steps };
}

/**
 * Convenience wrapper for tool-call inputs. Agents that shell out report the
 * argv they ran (`/bin/bash -lc "…"`), so the inner command is unwrapped first.
 */
export function shellSummaryFromInput(
  input: Record<string, unknown> | null,
  syntax = getShellSyntax(),
): ShellSummary | null {
  const raw = input?.command;
  if (typeof raw !== 'string' || raw.length === 0) return null;
  return summarizeShellCommand(unwrapShellCommand(raw), syntax);
}
